import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, Phone } from "@phosphor-icons/react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import TopStrip from "@/components/TopStrip";

const CATALOG2_EXTERNAL = "https://petsas-moto.car.gr/bikes/";

const navItems = [
  { to: "/thrust", label: "Thrust", testid: "nav-thrust-link" },
  { to: "/fb-mondial", label: "F.B. Mondial", testid: "nav-fbmondial-link" },
  { to: "/asus", label: "Asus", testid: "nav-asus-link" },
  { to: "/rdb-electric", label: "RDB Electric", testid: "nav-rdb-link" },
  { to: CATALOG2_EXTERNAL, label: "Μεταχειρισμένες", testid: "nav-catalog2-link", external: true },
  { to: "/service", label: "Σέρβις", testid: "nav-service-link" },
  { to: "/test-ride", label: "Δοκιμή", testid: "nav-test-ride-link" },
  { to: "/about", label: "Γκαράζ", testid: "nav-about-link" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const content = useSiteContent();
  const phone = content?.contact?.phone || "";
  const phoneHref = phone ? `tel:${phone.replace(/\s+/g, "")}` : "tel:";
  const topStripEnabled = !!content?.top_strip?.enabled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const renderNavItem = (it, isMobile = false) => {
    const baseCls = isMobile
      ? "text-base font-bold uppercase tracking-[0.18em]"
      : "text-[11px] lg:text-xs font-bold uppercase tracking-[0.16em] transition-colors whitespace-nowrap";

    if (it.external) {
      return (
        <a
          key={it.to}
          href={it.to}
          target="_blank"
          rel="noreferrer"
          data-testid={isMobile ? `mobile-${it.testid}` : it.testid}
          className={`${baseCls} text-white/70 hover:text-white`}
        >
          {it.label}
        </a>
      );
    }
    return (
      <NavLink
        key={it.to}
        to={it.to}
        end
        data-testid={isMobile ? `mobile-${it.testid}` : it.testid}
        className={({ isActive }) =>
          `${baseCls} ${
            isActive
              ? "text-[#E50000]"
              : isMobile ? "text-white/80" : "text-white/70 hover:text-white"
          }`
        }
      >
        {it.label}
      </NavLink>
    );
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <TopStrip />
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        {topStripEnabled ? (
          // Logo lives inside the top strip when it's enabled — leave a spacer here
          <span aria-hidden className="hidden md:block" />
        ) : (
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
            <img
              src="/logo.png"
              alt="Moto Petsas GP"
              className="h-10 md:h-12 w-auto transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </Link>
        )}

        <nav className="hidden md:flex items-center gap-5 lg:gap-7">
          {navItems.map((it) => renderNavItem(it, false))}
        </nav>

        <div className="hidden md:block">
          <a
            href={phoneHref}
            data-testid="header-call-cta"
            className="btn-primary text-xs"
          >
            <Phone size={14} weight="bold" className="mr-2" />
            {phone || "Καλέστε μας"}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Μενού"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2"
        >
          {open ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
        >
          <div className="flex flex-col px-6 py-6 gap-5">
            {navItems.map((it) => renderNavItem(it, true))}
            <a
              href={phoneHref}
              data-testid="mobile-call-cta"
              className="btn-primary text-xs mt-2 self-start"
            >
              <Phone size={14} weight="bold" className="mr-2" />
              {phone || "Καλέστε μας"}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
